import SearchIcon from 'assets/ver2/icons/search-icon.svg';
import {
  fetchReceiversFiltered,
  getBodyPayloadFiltered,
  invalidatePendingReceiversFetches,
} from 'components/Receivers/func';
import {
  BUSINESS_OF_RECEIVER_TYPE,
  DEBOUNCE_SEC,
  DESKTOP_MIN_WIDTH,
  ENTER_KEY_DOWN,
  PAGE_DEFAULT,
  RECEIVER_TABLE_SORT_ORDER_INITIAL,
} from 'constants';
import { searchTextValid } from 'helpers';
import useAuth from 'hooks/useAuth';
import useDebounceCallback from 'hooks/useDebounceCallback';
import useLang from 'hooks/useLang';
import { isEmpty } from 'lodash';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { arrayUtils } from 'utils';

const RECEIVER_SEARCH_INPUT_FIELD = 'receiver-search-input-field';

const normalizeSearchText = (value) =>
  (value ?? '').trim().split(' ').filter(Boolean).join(' ');

const SearchReceiver = ({
  applyFiltered,
  receivers,
  setReceivers,
  searchValue,
  setSearchValue,
  setTotalPages,
  setCurrentPage,
  setSortOrder,
  setTotalElements,
  setFetching = () => {},
  currentTab,
  setSearched = () => {},
  isSearchInvalid,
  setSearchSubmittedInvalid,
}) => {
  const { t } = useLang();
  const { token } = useAuth();

  // The valid text last submitted (whitespace-normalised, as the debounce
  // rewrites it), so a blur does not repeat that search.
  const lastSubmittedRef = useRef(null);

  useEffect(() => {
    if (!searchValue) lastSubmittedRef.current = null;
  }, [searchValue]);

  // Bumped when the input is cleared or flushed by an empty submit so a
  // debounce queued before it is dropped.
  const resetGenerationRef = useRef(0);

  // Reloads the list with an empty search value.
  const refetchUnsearchedList = () => {
    setSearched(false);
    // if (isEmpty(receivers)) return;

    let bodyPayload = getBodyPayloadFiltered(applyFiltered);

    bodyPayload = {
      ...bodyPayload,
      ...(currentTab === 1 && { gender: BUSINESS_OF_RECEIVER_TYPE }),
    };

    // setReceivers(null);
    fetchReceiversFiltered({
      token,
      body: bodyPayload,
      page: PAGE_DEFAULT,
      setReceivers,
      setCurrentPage,
      setTotalPages,
      setTotalElements,
      setFetching,
    });
  };

  // Latest text and reload for the deferred clear-reload, which fires after
  // renders that may have changed the text, filters or tab.
  const searchValueRef = useRef(searchValue);
  searchValueRef.current = searchValue;
  const refetchUnsearchedListRef = useRef(refetchUnsearchedList);
  refetchUnsearchedListRef.current = refetchUnsearchedList;
  // True from a clear until its reload runs or is dropped.
  const clearReloadPendingRef = useRef(false);
  // True while a clear shows the spinner only to keep this input mounted (on
  // desktop with no filters, an empty list and empty text hide it unless
  // fetching); unmounting would cancel the queued reload and lose the next
  // keystroke.
  const clearHeldSpinnerRef = useRef(false);
  const releaseClearSpinner = () => {
    if (!clearHeldSpinnerRef.current) return;
    clearHeldSpinnerRef.current = false;
    setFetching(false);
  };
  const runClearReload = () => {
    clearReloadPendingRef.current = false;
    clearHeldSpinnerRef.current = false;
    setFetching(true);
    refetchUnsearchedListRef.current();
  };

  // Reloads DEBOUNCE_SEC after the last clear if the box is then empty, so a
  // transient empty value (Backspace-style IME) neither blanks nor reloads.
  const { debounce: debounceClearReload } = useDebounceCallback(
    (generation) => {
      if (generation !== resetGenerationRef.current) return;
      if (searchValueRef.current) {
        clearReloadPendingRef.current = false;
        releaseClearSpinner();
        return;
      }
      runClearReload();
    },
    DEBOUNCE_SEC,
  );

  const { debounce } = useDebounceCallback((value, generation) => {
    if (generation !== resetGenerationRef.current) return;

    if (value) {
      value = value.trim();

      let values = value.split(' ');

      if (!arrayUtils.isArrayEmpty(values)) {
        values = arrayUtils.removeElement(values, '');

        value = values.join(' ');
      }

      setSearchValue(value);
    }

    // Reached with an empty value only when trimming left nothing.
    if (!value) {
      refetchUnsearchedList();
    }
  }, DEBOUNCE_SEC);

  const handleOnChange = (e) => {
    const { value } = e.target;
    setSearchSubmittedInvalid(false);

    if (!value) {
      // The reload runs on its own timer, so a later keystroke cannot restart
      // it; text present when it fires cancels it. The bump drops any debounce
      // queued by earlier text.
      resetGenerationRef.current += 1;
      clearReloadPendingRef.current = true;
      if (isEmpty(receivers)) {
        // A response already in flight can clear isFetching and, if it leaves
        // the list empty, hide this input (desktop, no filters, empty text),
        // cancelling its queued reload; drop it.
        invalidatePendingReceiversFetches();
        clearHeldSpinnerRef.current = true;
        setFetching(true);
      }
      debounceClearReload(resetGenerationRef.current);
    }

    setSearchValue(value);

    if (value) {
      releaseClearSpinner();
      debounce(value, resetGenerationRef.current);
    }
  };

  const searchByName = async (value) => {
    let bodyPayload = getBodyPayloadFiltered(applyFiltered);

    bodyPayload = {
      ...bodyPayload,
      name: value,
      ...(currentTab === 1 && { gender: BUSINESS_OF_RECEIVER_TYPE }),
    };

    setReceivers(null);
    fetchReceiversFiltered({
      token,
      body: bodyPayload,
      page: PAGE_DEFAULT,
      setReceivers,
      setCurrentPage,
      setTotalPages,
      setTotalElements,
      setFetching,
    });
  };
  const handleSearchReceiver = () => {
    // An empty submit while a clear-reload is pending runs that reload now.
    if (!searchValue && clearReloadPendingRef.current) {
      resetGenerationRef.current += 1;
      runClearReload();
      return;
    }

    if (!searchTextValid(searchValue)) {
      lastSubmittedRef.current = null;
      setSearchSubmittedInvalid(true);
      // Show the no-result state without a request. A dropped in-flight fetch
      // skips its setFetching(false), so clear it here. [] (not null) keeps
      // the page's initial-load effect off.
      invalidatePendingReceiversFetches();
      setReceivers([]);
      setFetching(false);
      setSortOrder(RECEIVER_TABLE_SORT_ORDER_INITIAL);
      return;
    }

    lastSubmittedRef.current = normalizeSearchText(searchValue);
    setSearchSubmittedInvalid(false);

    setFetching(true);

    setSearched(true);

    searchByName(searchValue);

    setSortOrder(RECEIVER_TABLE_SORT_ORDER_INITIAL);
  };

  const handleOnKeyDown = (e) => {
    const { key } = e;

    if (key === ENTER_KEY_DOWN) {
      handleSearchReceiver();
    }
  };

  // Leaving the field submits like Enter. Blank text, a window or tab switch
  // and a repeat of the last submitted text are left alone.
  const handleOnBlur = () => {
    if (!document.hasFocus()) return;
    if (!searchValue?.trim()) return;
    if (normalizeSearchText(searchValue) === lastSubmittedRef.current) return;

    handleSearchReceiver();
  };

  return (
    <SearchReceiverStyled>
      <SearchInput
        name={RECEIVER_SEARCH_INPUT_FIELD}
        type="text"
        value={searchValue || ''}
        placeholder={t('receivers_search_by_receiver_name')}
        onChange={handleOnChange}
        onKeyDown={handleOnKeyDown}
        onBlur={handleOnBlur}
      />
      <SearchIconStyled
        src={SearchIcon}
        width={24}
        height={24}
        alt=""
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleSearchReceiver}
      />
      {isSearchInvalid && (
        <SearchErrorStyled role="alert">
          {t('form_msg_search_validation')}
        </SearchErrorStyled>
      )}
    </SearchReceiverStyled>
  );
};

const SearchReceiverStyled = styled.div`
  position: relative;
  height: 44px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    height: unset;
  }
`;

const SearchInput = styled.input`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;

  color: var(--ds-c-grey-dark);
  border: 1px solid #b0b6bd;
  border-radius: 12px;
  background: var(--ds-c-white);
  width: calc(100% - 52px);
  height: 44px;
  padding: 10px;
  padding-right: 32px;
  float: left;

  &:focus,
  &:focus-visible {
    border: 1px solid #b0b6bd !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &::placeholder {
    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;

    color: var(--ds-c-blue-disabled) !important;

    @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
      font-size: 16px;
      line-height: 20px;
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
    width: 300px;
  }
`;
const SearchIconStyled = styled.img`
  position: absolute;
  top: 10px;
  left: calc(100% - 84px);
  cursor: pointer;
  user-select: none;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    left: 266px;
  }
`;

const SearchErrorStyled = styled.p`
  position: absolute;
  top: 48px;
  left: 0;
  width: calc(100% - 32px);
  margin: 0;
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: var(--ds-c-red);

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    width: 100%;
    font-size: 14px;
    line-height: 17px;
  }
`;

export default SearchReceiver;
